/**
 * 数値表示の共通ルール(UIデザインシステム設計書 v1.0 §2-3。docs/design-system.md)。
 * 画面ごとに桁数や単位を変えないため、表示用の文字列はすべてここで作る。
 *
 * | データ     | 表示例        | 規則                       |
 * |------------|---------------|----------------------------|
 * | 件数       | 1,387件       | カンマ区切り・整数         |
 * | 金額       | ¥1,156,085    | 整数(表では右揃え)       |
 * | 原価率など | 44.1%         | 小数第1位                  |
 * | 率の差     | +4.6pt        | % ではなく pt              |
 * | 時間       | 154.1h        | 小数第1位                  |
 * | 秒の長さ   | 0:09:22       | 時:分:秒(通話・後処理など)|
 * | 時間帯     | 9:00–18:00    | 24時間表記                 |
 * | 時給       | ¥1,300/h      | 単位を明示                 |
 * | 千円・万円 | 1,234.6       | 小数第1位(単位は見出し)  |
 *
 * 値が無いときは区別して出す: データなし=「—」、未入力=「未入力」。0 は 0 として出す。
 * マイナスは読みやすい全角幅のマイナス記号「−」(U+2212)を使う。
 */

/** データなし(集計対象が無い・判定できない) */
export const NO_DATA = '—';
/** 未入力(入力が必要なのにまだ無い) */
export const NOT_ENTERED = '未入力';
const MINUS = '−';

export type Missing = 'noData' | 'notEntered';
type Base = { missing?: Missing };

const missingText = (m: Missing | undefined) => (m === 'notEntered' ? NOT_ENTERED : NO_DATA);
const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** 四捨五入(0.5 は絶対値を切り上げ。浮動小数の誤差 1.005 → 1.01 を補正) */
export function roundTo(value: number, digits = 0): number {
  const p = 10 ** digits;
  const r = Math.round(Math.abs(value) * p + Number.EPSILON * p) / p;
  return value < 0 ? -r : r;
}

function grouped(abs: number, digits: number): string {
  return abs.toLocaleString('ja-JP', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** 符号付きの数(マイナスは「−」)。-0 は 0 */
function signed(value: number, digits: number, prefix = ''): string {
  const r = roundTo(value, digits);
  const body = `${prefix}${grouped(Math.abs(r), digits)}`;
  return r < 0 ? `${MINUS}${body}` : body;
}

/** 件数: 1,387 / 1,387件(unit を付けたとき) */
export function formatCount(value: number | null | undefined, opts: Base & { unit?: string } = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  return `${signed(value, 0)}${opts.unit ?? ''}`;
}

/** 金額: ¥1,156,085 / −¥1,234 */
export function formatCurrency(value: number | null | undefined, opts: Base = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  return signed(value, 0, '¥');
}

/** 率(値は % の数。44.14 → 44.1%) */
export function formatPercent(value: number | null | undefined, opts: Base & { digits?: number } = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  return `${signed(value, opts.digits ?? 1)}%`;
}

/** 率の差(pt): +4.6pt / −1.2pt / ±0.0pt */
export function formatPointDiff(value: number | null | undefined, opts: Base & { digits?: number } = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  const digits = opts.digits ?? 1;
  const r = roundTo(value, digits);
  if (r === 0) return `±${grouped(0, digits)}pt`;
  return `${r > 0 ? '+' : MINUS}${grouped(Math.abs(r), digits)}pt`;
}

/** 件数の差: +12件 / −3件 / ±0件 */
export function formatCountDiff(value: number | null | undefined, opts: Base & { unit?: string } = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  const r = roundTo(value, 0);
  if (r === 0) return `±0${opts.unit ?? ''}`;
  return `${r > 0 ? '+' : MINUS}${grouped(Math.abs(r), 0)}${opts.unit ?? ''}`;
}

/** 金額の単位の切り替え(PL など) */
export type MoneyScale = 'yen' | 'k' | 'man';

/**
 * 金額を単位に合わせて出す。円=¥1,234,567 / 千円=1,234.6 / 万円=123.5。
 * 千円・万円は単位を見出しに出し、桁がそろうよう小数第1位までそろえる。
 */
export function formatScaledCurrency(value: number | null | undefined, scale: MoneyScale, opts: Base = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  if (scale === 'yen') return formatCurrency(value);
  return signed(value / (scale === 'k' ? 1000 : 10000), 1);
}

/** 小数(1時間あたりの件数など): 2.33 / 2.33件/h */
export function formatDecimal(value: number | null | undefined, opts: Base & { digits?: number; unit?: string } = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  return `${signed(value, opts.digits ?? 2)}${opts.unit ?? ''}`;
}

/** 時間(h): 154.1h */
export function formatHours(value: number | null | undefined, opts: Base = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  return `${signed(value, 1)}h`;
}

/** 秒単位の長さ(通話・後処理など BlueBean の時間): 1:23:45(時:分:秒)。勤務時間の合計は formatHours */
export function formatDuration(sec: number | null | undefined, opts: Base = {}): string {
  if (!isNumber(sec)) return missingText(opts.missing);
  const s = Math.round(Math.abs(sec));
  const body = `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  return sec < 0 && s > 0 ? `${MINUS}${body}` : body;
}

/** 時刻(24時間表記): 9 → 9:00 / 9.5 → 9:30 / 18.25 → 18:15 */
export function formatClock(hours: number | null | undefined, opts: Base = {}): string {
  if (!isNumber(hours)) return missingText(opts.missing);
  const totalMin = Math.round(hours * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin - h * 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/** 時間帯: 9:00–18:00(区切りは en dash) */
export function formatTimeRange(start: number | null | undefined, end: number | null | undefined, opts: Base = {}): string {
  if (!isNumber(start) || !isNumber(end)) return missingText(opts.missing);
  return `${formatClock(start)}–${formatClock(end)}`;
}

/** 時給: ¥1,300/h */
export function formatHourlyWage(value: number | null | undefined, opts: Base = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  return `${formatCurrency(value)}/h`;
}

/** 単価: ¥400/件 */
export function formatUnitPrice(value: number | null | undefined, opts: Base & { per?: string } = {}): string {
  if (!isNumber(value)) return missingText(opts.missing);
  return `${formatCurrency(value)}/${opts.per ?? '件'}`;
}

/** 数値の状態(色やスクリーンリーダー向けの補足に使う): データなし / マイナス / 0 / プラス */
export type NumericState = 'missing' | 'negative' | 'zero' | 'positive';
export function numericState(value: number | null | undefined): NumericState {
  if (!isNumber(value)) return 'missing';
  const r = roundTo(value, 6);
  return r < 0 ? 'negative' : r === 0 ? 'zero' : 'positive';
}
